import { supabase } from '@/lib/supabase/client';
import type { ComponentPricing, ComponentPricingInsert, ComponentPricingUpdate } from '@/types/component-pricing';
import { componentsService } from './components';

export const componentPricingService = {
  async getAll(): Promise<ComponentPricing[]> {
    try {
      const { data, error } = await supabase.from('component_pricing').select('*').order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching component pricing:', error);
        return [];
      }

      return (data as ComponentPricing[]) || [];
    } catch (error) {
      console.error('Error fetching component pricing:', error);
      return [];
    }
  },

  async getByComponentId(componentId: string): Promise<ComponentPricing | null> {
    try {
      const { data, error } = await supabase.from('component_pricing').select('*').eq('component_id', componentId).single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching component pricing:', error);
        return null;
      }

      return (data as ComponentPricing) || null;
    } catch (error) {
      console.error('Error fetching component pricing:', error);
      return null;
    }
  },

  async create(data: ComponentPricingInsert): Promise<ComponentPricing> {
    try {
      const insertData = {
        component_id: data.component_id,
        price: data.price,
        vendor: data.vendor ?? null,
        vendor_url: data.vendor_url ?? null,
        last_synced_at: data.last_synced_at ?? null,
        synced_from_sheet: data.synced_from_sheet ?? false,
      };

      const { data: newPricing, error } = await supabase.from('component_pricing').insert(insertData).select().single();

      if (error) {
        console.error('Error creating component pricing:', error);
        throw new Error(`Failed to create component pricing: ${error.message}`);
      }

      return newPricing as ComponentPricing;
    } catch (error) {
      console.error('Error creating component pricing:', error);
      throw error;
    }
  },

  async updatePrice(componentId: string, price: number): Promise<ComponentPricing> {
    try {
      const { data: updatedPricing, error } = await supabase
        .from('component_pricing')
        .update({ price })
        .eq('component_id', componentId)
        .select()
        .single();

      if (error) {
        console.error('Error updating component price:', error);
        throw new Error(`Failed to update component price: ${error.message}`);
      }

      if (!updatedPricing) {
        throw new Error('Component pricing not found');
      }

      return updatedPricing as ComponentPricing;
    } catch (error) {
      console.error('Error updating component price:', error);
      throw error;
    }
  },

  async update(componentId: string, data: ComponentPricingUpdate): Promise<ComponentPricing> {
    try {
      const updateData: Record<string, unknown> = {};

      // Only include fields that are provided
      if (data.price !== undefined) updateData.price = data.price;
      if (data.vendor !== undefined) updateData.vendor = data.vendor;
      if (data.vendor_url !== undefined) updateData.vendor_url = data.vendor_url;
      if (data.last_synced_at !== undefined) updateData.last_synced_at = data.last_synced_at;
      if (data.synced_from_sheet !== undefined) updateData.synced_from_sheet = data.synced_from_sheet;

      const { data: updatedPricing, error } = await supabase
        .from('component_pricing')
        .update(updateData)
        .eq('component_id', componentId)
        .select()
        .single();

      if (error) {
        console.error('Error updating component pricing:', error);
        throw new Error(`Failed to update component pricing: ${error.message}`);
      }

      if (!updatedPricing) {
        throw new Error('Component pricing not found');
      }

      return updatedPricing as ComponentPricing;
    } catch (error) {
      console.error('Error updating component pricing:', error);
      throw error;
    }
  },

  async delete(componentId: string): Promise<void> {
    try {
      const { error } = await supabase.from('component_pricing').delete().eq('component_id', componentId);

      if (error) {
        console.error('Error deleting component pricing:', error);
        throw new Error(`Failed to delete component pricing: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting component pricing:', error);
      throw error;
    }
  },

  async getAffectedDisplayTypes(componentId: string): Promise<Array<{ id: string; name: string }>> {
    try {
      // Get all display_components that use this component
      const { data: displayComponents, error: dcError } = await supabase
        .from('display_components')
        .select('display_type_id')
        .eq('component_id', componentId);

      if (dcError) {
        console.error('Error fetching display components:', dcError);
        return [];
      }

      if (!displayComponents || displayComponents.length === 0) {
        return [];
      }

      // Get unique display type IDs
      const displayTypeIds = [...new Set(displayComponents.map((dc) => dc.display_type_id))];

      // Fetch display types
      const { data: displayTypes, error: dtError } = await supabase
        .from('display_types')
        .select('id, name')
        .in('id', displayTypeIds);

      if (dtError) {
        console.error('Error fetching display types:', dtError);
        return [];
      }

      return (displayTypes as Array<{ id: string; name: string }>) || [];
    } catch (error) {
      console.error('Error getting affected display types:', error);
      return [];
    }
  },

  async getPriceForComponent(componentId: string): Promise<number> {
    try {
      // First, try to get price from component_pricing
      const pricing = await this.getByComponentId(componentId);
      if (pricing && pricing.price) {
        return pricing.price;
      }

      // Fall back to component.default_cost
      const component = await componentsService.getById(componentId);
      if (component && component.default_cost !== null) {
        return component.default_cost;
      }

      // If neither exists, throw an error
      throw new Error(`No price found for component ${componentId}`);
    } catch (error) {
      console.error('Error getting price for component:', error);
      throw error;
    }
  },
};
